import { useCallback, useState } from "react";
import Cropper from "react-easy-crop";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Slider } from "@/components/ui/slider";
import { MagnifyingGlassPlus } from "@phosphor-icons/react";

async function getCroppedBlob(src, area) {
  const image = await new Promise((res, rej) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => res(img);
    img.onerror = rej;
    img.src = src;
  });
  const canvas = document.createElement("canvas");
  // Normalize output to 800x600 (4:3) for a consistent catalog look
  canvas.width = 800;
  canvas.height = 600;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(image, area.x, area.y, area.width, area.height, 0, 0, canvas.width, canvas.height);
  return new Promise((res) => canvas.toBlob((b) => res(b), "image/jpeg", 0.9));
}

export function ImageCropper({ open, src, onCancel, onCropped }) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [pixels, setPixels] = useState(null);
  const [working, setWorking] = useState(false);

  const onComplete = useCallback((_area, areaPixels) => setPixels(areaPixels), []);

  const apply = async () => {
    if (!pixels) return;
    setWorking(true);
    const blob = await getCroppedBlob(src, pixels);
    setWorking(false);
    onCropped(blob);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onCancel()}>
      <DialogContent className="bg-card border-border max-w-lg" data-testid="crop-dialog">
        <DialogHeader>
          <DialogTitle className="font-display font-black text-2xl uppercase tracking-tight">Position & crop</DialogTitle>
        </DialogHeader>
        <div className="relative w-full h-[320px] bg-black border border-border">
          {src && (
            <Cropper
              image={src}
              crop={crop}
              zoom={zoom}
              aspect={4 / 3}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onComplete}
              objectFit="cover"
            />
          )}
        </div>
        <div className="flex items-center gap-3 mt-2">
          <MagnifyingGlassPlus size={20} className="text-primary" />
          <Slider min={1} max={3} step={0.01} value={[zoom]} onValueChange={(v) => setZoom(v[0])} className="flex-1" data-testid="crop-zoom" />
        </div>
        <div className="flex gap-3 mt-2">
          <button onClick={onCancel} className="flex-1 border border-border font-bold uppercase text-sm tracking-wide py-3 rounded-sm hover:border-primary/60 transition-colors" data-testid="crop-cancel">Cancel</button>
          <button onClick={apply} disabled={working} className="flex-1 bg-primary text-white font-bold uppercase text-sm tracking-wide py-3 rounded-sm hover:bg-red-500 transition-colors disabled:opacity-60" data-testid="crop-apply">
            {working ? "Cropping…" : "Apply crop"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
